import { v4 as uuid } from 'uuid'
import supabase from '@/etc/supabase'

const { VUE_APP_AI_BACKEND } = process.env

const ANONYMOUS_ID_KEY = 'vyby/analytics/anonymousId'
const SESSION_ID_KEY = 'vyby/analytics/sessionId'
const MAX_RECENT_EVENTS = 50

export const ANALYTICS_EVENTS = Object.freeze({
  USER_SIGNED_IN: 'user_signed_in',
  SESSION_STARTED: 'session_started',
  SESSION_ENDED: 'session_ended',
  PAGE_VIEWED: 'page_viewed',
  WAITLIST_JOINED: 'waitlist_joined',
  PROJECT_CREATED: 'project_created',
  PROJECT_OPENED: 'project_opened',
  EDITOR_OPENED: 'editor_opened',
  EXPORT_COMPLETED: 'export_completed',
  INVITE_VIEWED: 'invite_viewed',
  BOUNTY_VIEWED: 'bounty_viewed',
})

const memoryStorage = new Map()
const state = {
  userId: null,
  token: null,
  installed: false,
  startedAt: Date.now(),
  recent: [],
}

const storageValue = (storage, key, fallback) => {
  try {
    const existing = storage?.getItem(key)
    if (existing) return existing
    storage?.setItem(key, fallback)
    return fallback
  } catch (error) {
    if (!memoryStorage.has(key)) memoryStorage.set(key, fallback)
    return memoryStorage.get(key)
  }
}

const anonymousId = () =>
  storageValue(
    typeof window !== 'undefined' ? window.localStorage : null,
    ANONYMOUS_ID_KEY,
    uuid(),
  )

const sessionId = () =>
  storageValue(
    typeof window !== 'undefined' ? window.sessionStorage : null,
    SESSION_ID_KEY,
    uuid(),
  )

const endpoint = () => {
  const base = `${VUE_APP_AI_BACKEND || ''}`.trim().replace(/\/+$/, '')
  return base ? `${base}/vbapi/analytics/events` : ''
}

const pushRecent = (entry) => {
  state.recent.push(entry)
  if (state.recent.length > MAX_RECENT_EVENTS) state.recent.shift()
}

const resolveToken = async () => {
  if (state.token) return state.token
  try {
    const { data } = await supabase.auth.getSession()
    state.userId = data?.session?.user?.id || null
    state.token = data?.session?.access_token || null
  } catch (error) {
    state.userId = null
    state.token = null
  }
  return state.token
}

export const setAnalyticsAuth = (session) => {
  state.userId = session?.user?.id || null
  state.token = session?.access_token || null
}

export const trackAnalyticsEvent = async (
  eventName,
  {
    projectId = null,
    entityType = null,
    entityId = null,
    metadata = {},
    keepalive = false,
  } = {},
) => {
  const eventId = uuid()
  const occurredAt = new Date().toISOString()
  const recent = {
    eventId,
    eventName,
    occurredAt,
    status: 'queued',
  }
  pushRecent(recent)

  const url = endpoint()
  if (!url || typeof fetch !== 'function') {
    recent.status = 'skipped'
    return { ok: false, skipped: true }
  }

  const token = await resolveToken()
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        eventId,
        eventName,
        eventVersion: 1,
        anonymousId: anonymousId(),
        sessionId: sessionId(),
        projectId,
        entityType,
        entityId,
        occurredAt,
        metadata,
      }),
      keepalive,
    })
    recent.status = response.ok ? 'sent' : 'rejected'
    recent.httpStatus = response.status
    return { ok: response.ok, status: response.status }
  } catch (error) {
    recent.status = 'failed'
    return { ok: false, error: 'ANALYTICS_UNAVAILABLE' }
  }
}

export const installAnalytics = ({ router } = {}) => {
  if (state.installed || typeof window === 'undefined') return
  state.installed = true
  state.startedAt = Date.now()

  const currentRoute = router?.currentRoute?.value
  void trackAnalyticsEvent(ANALYTICS_EVENTS.SESSION_STARTED, {
    metadata: {
      entry_route: `${currentRoute?.name || currentRoute?.path || 'unknown'}`,
    },
  })

  if (router) {
    router.afterEach((to) => {
      void trackAnalyticsEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
        metadata: {
          route_name: `${to?.name || to?.path || 'unknown'}`,
        },
      })
    })
  }

  window.addEventListener('pagehide', () => {
    void trackAnalyticsEvent(ANALYTICS_EVENTS.SESSION_ENDED, {
      metadata: {
        duration_ms: Math.max(0, Date.now() - state.startedAt),
      },
      keepalive: true,
    })
  })
}

export const analyticsDebugSnapshot = () => ({
  userId: state.userId,
  anonymousId: anonymousId(),
  sessionId: sessionId(),
  endpoint: endpoint(),
  recent: state.recent.map((event) => ({ ...event })),
})

export const installAnalyticsDebug = () => {
  if (typeof window === 'undefined') return
  window.__vyby = window.__vyby || {}
  window.__vyby.analytics = {
    snapshot: analyticsDebugSnapshot,
    track: (eventName, options = {}) => trackAnalyticsEvent(eventName, options),
    events: ANALYTICS_EVENTS,
  }
}
